import { AxiosRequestConfig, AxiosResponse } from 'axios';

import api from './api';

interface TelasProps {
  url: string;
}

interface PortalSessionUser {
  roles: string[];
  telas: TelasProps[];
}

const PORTAL_STORAGE_SESSION_TOKEN = '@PortalUnicred/sessionToken';
const PORTAL_STORAGE_USER = '@PortalUnicred/user';
const PORTAL_STORAGE_EXPIRE_HREF = '@PortalUnicred/expire';

const SESSION_EXPIRED_MESSAGE =
  'A sua sessão expirou, por favor efetue login novamente.';
const UNICRED_UNAUTHENTICATED_DETAIL_DOUBLE_SPACE =
  'Usuário não autenticado nos sistemas da  Unicred.';
const UNICRED_UNAUTHENTICATED_DETAIL_SINGLE_SPACE =
  'Usuário não autenticado nos sistemas da Unicred.';
const INTERNO_ALLOWED_ROOT_SEGMENTS = [
  '/home',
  '/perfil',
  '/telas',
  '/cooperado',
];
const NON_INTERNO_ALLOWED_ROOT_SEGMENTS = ['/home', '/cooperado'];

function getBrowserPathFirstSegment(): string {
  return `/${window.location.pathname.split('/')[1]}`;
}

function extractTelaPathPrefixes(user: PortalSessionUser): string[] {
  return user.telas.map(tela => tela.url.replace('/listar', ''));
}

function isPortalPathAllowed(
  pathName: string,
  telaPathPrefixes: string[],
  primaryRole: string,
): boolean {
  if (telaPathPrefixes.includes(pathName)) {
    return true;
  }
  if (primaryRole === 'interno') {
    return INTERNO_ALLOWED_ROOT_SEGMENTS.includes(pathName);
  }
  return NON_INTERNO_ALLOWED_ROOT_SEGMENTS.includes(pathName);
}

function applyAuthenticatedPortalRequest(
  config: AxiosRequestConfig,
  token: string,
  user: PortalSessionUser,
): void {
  config.headers.authorization = `Bearer ${token}`;
  const pathName = getBrowserPathFirstSegment();
  const telaPathPrefixes = extractTelaPathPrefixes(user);
  if (!isPortalPathAllowed(pathName, telaPathPrefixes, user.roles[0])) {
    window.location.href = `${window.location.origin}/acesso/negado`;
  }
}

function isPutToOwnProfilePath(response: AxiosResponse): boolean {
  const profileId = response.data?.id;
  if (profileId === undefined || profileId === null) {
    return false;
  }
  return (
    response.config.method === 'put' &&
    response.config.url === `/perfil/${profileId}`
  );
}

async function persistPortalUserFromAutenticado(): Promise<void> {
  const { data } = await api.get('/autenticado');
  localStorage.removeItem(PORTAL_STORAGE_USER);
  localStorage.setItem(PORTAL_STORAGE_USER, JSON.stringify(data));
}

function shouldRedirectToSessionExpired(
  status: number,
  data: unknown,
): boolean {
  const body = data as {
    message?: string;
    error?: string;
  } | null;
  if (status === 406) {
    return true;
  }
  if (body?.error === 'Not Acceptable') {
    return true;
  }
  return body?.message === SESSION_EXPIRED_MESSAGE;
}

function shouldRedirectToAccessDeniedWithoutReturnUrl(
  status: number,
  data: unknown,
): boolean {
  const body = data as {
    message?: string;
    details?: string[];
  } | null;
  if (status === 403) {
    return true;
  }
  return (
    body?.details?.[0] === UNICRED_UNAUTHENTICATED_DETAIL_DOUBLE_SPACE &&
    body?.message === 'UNAUTHORIZED'
  );
}

function shouldRedirectToAccessDeniedWithReturnUrl(data: unknown): boolean {
  const body = data as { details?: string[] } | null;
  return body?.details?.[0] === UNICRED_UNAUTHENTICATED_DETAIL_SINGLE_SPACE;
}

export function createInterceptors(): void {
  api.interceptors.request.use((config: AxiosRequestConfig) => {
    try {
      const token = localStorage.getItem(PORTAL_STORAGE_SESSION_TOKEN);
      if (!token) {
        return config;
      }
      const user = JSON.parse(
        localStorage.getItem(PORTAL_STORAGE_USER) as string,
      ) as PortalSessionUser;
      applyAuthenticatedPortalRequest(config, token, user);
      return config;
    } catch (err) {
      return Promise.reject(err);
    }
  });

  api.interceptors.response.use(
    async response => {
      if (isPutToOwnProfilePath(response)) {
        await persistPortalUserFromAutenticado();
      }
      return response;
    },
    async error => {
      if (error.response === undefined) {
        return Promise.reject(error);
      }
      const { status, data } = error.response;

      if (shouldRedirectToSessionExpired(status, data)) {
        window.location.href = `${window.location.origin}/acesso/expirado`;
        localStorage.removeItem(PORTAL_STORAGE_SESSION_TOKEN);
        localStorage.removeItem(PORTAL_STORAGE_USER);
        return;
      }
      if (shouldRedirectToAccessDeniedWithoutReturnUrl(status, data)) {
        window.location.href = `${window.location.origin}/acesso/negado`;
        return;
      }
      if (shouldRedirectToAccessDeniedWithReturnUrl(data)) {
        localStorage.setItem(PORTAL_STORAGE_EXPIRE_HREF, window.location.href);
        window.location.href = `${window.location.origin}/acesso/negado`;
      }
    },
  );
}
